import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { Modal } from "../components/Modal";
import type { MeetingListItem, SystemStatus } from "../types";
import {
  fmtDateParts,
  initials,
  isLiveNow,
  statusBadge,
  syncBadge,
} from "../components/util";

type Scope = "upcoming" | "past";

export function MeetingsPage({ status }: { status: SystemStatus | null }) {
  const [scope, setScope] = useState<Scope>("upcoming");
  const [meetings, setMeetings] = useState<MeetingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [offlineOpen, setOfflineOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .listMeetings(scope)
      .then((m) => {
        setMeetings(m);
        setError("");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [scope]);

  useEffect(load, [load]);

  // Пока что-то обрабатывается, подтягиваем список — конвейер работает в фоне
  useEffect(() => {
    if (!meetings.some((m) => m.is_processing)) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [meetings, load]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Встречи</h1>
          <p>
            {scope === "upcoming"
              ? "Очные встречи и созвоны. Записи проще всего присылать боту — он сам заведёт карточку."
              : "Прошедшие встречи с записями, расшифровками и разбором."}
          </p>
        </div>
        <div className="row">
          <button className="primary" onClick={() => setOfflineOpen(true)}>
            + Очная встреча
          </button>
          <button onClick={() => navigate("/meetings/new")}>+ Созвон в Zoom</button>
        </div>
      </div>

      <div className="tabs">
        <button
          className={scope === "upcoming" ? "active" : ""}
          onClick={() => setScope("upcoming")}
        >
          Ближайшие
        </button>
        <button
          className={scope === "past" ? "active" : ""}
          onClick={() => setScope("past")}
        >
          Прошедшие
        </button>
      </div>

      {error && <div className="banner red">{error}</div>}

      {loading && meetings.length === 0 ? (
        <div className="card">
          <div className="empty">
            <span className="spinner" /> Загружаю…
          </div>
        </div>
      ) : meetings.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>
              {scope === "upcoming" ? "Ближайших встреч нет" : "Прошедших встреч нет"}
            </h3>
            <p>
              {scope === "upcoming"
                ? "Создайте встречу — она заведётся в Zoom, а ссылка появится здесь."
                : "Как только встреча завершится, запись и разбор появятся тут."}
            </p>
            {scope === "upcoming" && (
              <button className="primary" onClick={() => navigate("/meetings/new")}>
                Создать встречу
              </button>
            )}
          </div>
        </div>
      ) : (
        meetings.map((m) => (
          <MeetingCard key={m.id} meeting={m} zoomReady={!!status?.zoom.configured} />
        ))
      )}

      {offlineOpen && (
        <OfflineMeetingModal
          onClose={() => setOfflineOpen(false)}
          onCreated={(id) => navigate(`/meetings/${id}`)}
        />
      )}
    </>
  );
}

function MeetingCard({
  meeting,
  zoomReady,
}: {
  meeting: MeetingListItem;
  zoomReady: boolean;
}) {
  const when = fmtDateParts(meeting.start_time);
  const badge = statusBadge(meeting);
  const sync = syncBadge(meeting);
  const live = isLiveNow(meeting);
  const upcoming = meeting.status === "scheduled" || meeting.status === "live";

  return (
    <div className="card">
      <div className="card-body">
        <div className="meeting-row">
          <div className="meeting-when">
            <div className="d">{when.day}</div>
            <div className="m">
              {when.month} · {when.weekday}
            </div>
            <div className="t">{when.time}</div>
          </div>

          <div className="meeting-main">
            <h3>
              <Link to={`/meetings/${meeting.id}`}>{meeting.topic}</Link>
            </h3>
            <div className="sub">
              {meeting.duration_min} мин · {meeting.timezone}
              {meeting.agenda ? ` · ${meeting.agenda.slice(0, 90)}` : ""}
            </div>

            <div className="row" style={{ marginTop: 8 }}>
              <span className={`badge ${badge.cls}`}>
                {live && <span className="dot pulse" />}
                {badge.label}
              </span>
              {sync && (
                <span className={`badge ${sync.cls}`} title={sync.title}>
                  {sync.label}
                </span>
              )}
              {meeting.kind === "offline" && (
                <span
                  className="badge lime"
                  title="Очная встреча — запись загружается вручную или через бота"
                >
                  очная
                </span>
              )}
              {meeting.source === "zoom" && (
                <span
                  className="badge"
                  title="Встреча создана не в сервисе, а в Zoom — сервис подтянул её сам"
                >
                  создана в Zoom
                </span>
              )}
              {meeting.auto_recording === "cloud" && upcoming && (
                <span className="badge" title="Запись включится автоматически">
                  ● автозапись
                </span>
              )}
              {meeting.is_processing && (
                <span className="badge amber">
                  <span className="spinner" /> обрабатывается
                </span>
              )}
              {meeting.has_recording && <span className="badge">запись</span>}
              {meeting.has_transcript && <span className="badge">расшифровка</span>}
              {meeting.has_summary && (
                <span className="badge blue">выжимка готова</span>
              )}
            </div>

            {meeting.participants.length > 0 && (
              <div className="avatars">
                {meeting.participants.slice(0, 8).map((p) => (
                  <span
                    key={p.id}
                    className={`avatar ${p.kind === "external" ? "external" : ""}`}
                    title={`${p.name} <${p.email}>`}
                  >
                    {initials(p.name)}
                  </span>
                ))}
                {meeting.participants.length > 8 && (
                  <span className="avatar" title="ещё участники">
                    +{meeting.participants.length - 8}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="row nowrap meeting-actions">
            {upcoming && meeting.join_url && (
              <a
                className="btn btn-primary"
                href={meeting.join_url}
                target="_blank"
                rel="noreferrer"
              >
                Подключиться
              </a>
            )}
            {upcoming && !meeting.join_url && (
              <span
                className="badge amber"
                title={
                  zoomReady
                    ? "Ссылка появится, когда встреча заведётся в Zoom"
                    : "Заполните ключи Zoom в .env"
                }
              >
                ссылки пока нет
              </span>
            )}
            <Link className="btn" to={`/meetings/${meeting.id}`}>
              Открыть
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Очная встреча заводится одним полем: с кем говорили. Всё остальное —
 * компанию, потребности, договорённости — сервис достанет из записи сам.
 */
function OfflineMeetingModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [note, setNote] = useState("");
  const [profile, setProfile] = useState<"expo" | "meeting">("expo");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const meeting = await api.createOfflineMeeting({
        topic: note.split("\n")[0].slice(0, 120),
        context_note: note,
        analysis_profile: profile,
        participant_ids: [],
      });
      onCreated(meeting.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Очная встреча"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Отмена</button>
          <button className="primary" form="offline-form" type="submit" disabled={busy}>
            {busy && <span className="spinner" />} Создать
          </button>
        </>
      }
    >
      <form id="offline-form" onSubmit={submit}>
        <div className="banner blue" style={{ marginTop: 0 }}>
          <span>💬</span>
          <div>
            <strong>Проще — через бота</strong>
            Сотруднику достаточно переслать запись в телеграм: карточка встречи
            и карточка клиента создадутся сами, заводить ничего не нужно.
            Эта форма — для случая, когда файл под рукой на компьютере.
          </div>
        </div>

        <p className="small muted">
          В Zoom такая встреча не заводится: сервис расшифрует загруженный
          разговор и соберёт карточку клиента.
        </p>

        <div className="field">
          <label htmlFor="o-note">С кем встречались</label>
          <input
            id="o-note"
            autoFocus
            required
            value={note}
            placeholder="Ромашка, Сергей Петров, закупки"
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="muted small" style={{ marginTop: 4 }}>
            Компания, имя, должность — что знаете. Это надёжнее, чем распознанная
            в шуме речь.
          </div>
        </div>

        <div className="field">
          <label htmlFor="o-profile">Что нужно на выходе</label>
          <select
            id="o-profile"
            value={profile}
            onChange={(e) => setProfile(e.target.value as "expo" | "meeting")}
          >
            <option value="expo">Карточка клиента — для выставок и продаж</option>
            <option value="meeting">Протокол — темы, договорённости, задачи</option>
          </select>
        </div>

        {error && <div className="error-text">{error}</div>}
      </form>
    </Modal>
  );
}
